import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuthToken } from '../../services/authUtils';
import { useAlpaca } from '../../context/AlpacaContext';
import { API_URL } from '../../config';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import styles from './OAuthCallback.module.css';

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshStatus } = useAlpaca();
  const [error, setError] = useState<string | null>(null);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        setError('Missing authorization code or state parameter.');
        return;
      }

      // Validate CSRF state
      const storedState = sessionStorage.getItem('alpaca_oauth_state');
      if (state !== storedState) {
        setError('Security validation failed. The state parameter does not match. Please try connecting again.');
        return;
      }

      // Clean up stored state
      sessionStorage.removeItem('alpaca_oauth_state');
      const env = sessionStorage.getItem('alpaca_oauth_env') || 'paper';
      sessionStorage.removeItem('alpaca_oauth_env');

      // PKCE: retrieve code_verifier for token exchange
      const codeVerifier = sessionStorage.getItem('alpaca_pkce_verifier') || '';
      sessionStorage.removeItem('alpaca_pkce_verifier');

      try {
        const token = await getAuthToken();
        if (!token) {
          setError('Authentication expired. Please sign in and try again.');
          return;
        }

        const resp = await fetch(`${API_URL}/api/alpaca/oauth/callback`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, state: storedState || state, env, code_verifier: codeVerifier }),
        });

        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body.error || body.detail || `Token exchange failed (${resp.status})`);
        }

        // Refresh Alpaca connection status
        await refreshStatus();

        // Navigate to onboarding success step
        navigate('/onboarding', { state: { step: 'connected' }, replace: true });
      } catch (err: any) {
        console.error('OAuth callback error:', err);
        setError(err.message || 'Failed to connect your Alpaca account. Please try again.');
      }
    };

    processCallback();
  }, [searchParams, navigate, refreshStatus]);

  const handleRetry = () => {
    navigate('/onboarding', { replace: true });
  };

  if (error) {
    return (
      <div className={styles.callback}>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>
            <span className={styles.errorIconX}>&times;</span>
          </div>
          <h2 className={styles.errorTitle}>Connection Failed</h2>
          <p className={styles.errorMessage}>{error}</p>
          <button className={styles.btnRetry} onClick={handleRetry}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.callback}>
      <SignalLoader message="Connecting your Alpaca account..." />
    </div>
  );
};

export default OAuthCallback;
